import React, { useState, useEffect } from 'react';
import { Button } from "../../../components/ui/Button";
import { FileText, Loader2 } from "lucide-react";
import { API_BASE_URL } from "../../../services/api";

export const PdfViewer = ({ selectedDocument, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedDocument || !selectedDocument.fileUrl) return;
    
    setLoading(true);
    
    // Convert relative URL to full backend API URL
    const relativeUrl = selectedDocument.fileUrl.startsWith('http') 
      ? selectedDocument.fileUrl.replace(/^.*\/\/[^\/]+/, '')
      : selectedDocument.fileUrl;
      
    // Handle the /api prefix correctly since API_BASE_URL usually doesn't have it
    const apiPath = relativeUrl.startsWith('/api') || relativeUrl.startsWith('/uploads') ? relativeUrl : `/api${relativeUrl}`;
    
    // Add the authentication token to the query params so the backend can authorize the iframe request
    const token = localStorage.getItem('blum_token') || localStorage.getItem('token') || '';
    const separator = apiPath.includes('?') ? '&' : '?';
    const fullUrl = token ? `${API_BASE_URL}${apiPath}${separator}token=${token}` : `${API_BASE_URL}${apiPath}`;
    
    setPdfUrl(fullUrl);
    
    // We can hide the loading state after a brief delay since the iframe will show its own loader
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [selectedDocument]);

  const handleDownload = () => {
    if (selectedDocument?.fileUrl) {
      downloadPdfFromUrl(selectedDocument.fileUrl, selectedDocument.title || 'Document.pdf');
    }
  };

  if (!selectedDocument) return null;

  return (
    <div className="flex-1 bg-white overflow-hidden flex flex-col h-full rounded-b-lg sm:rounded-b-xl">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50/80 shrink-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <FileText size={18} />
          </div>
          <h3 className="text-sm font-medium text-slate-800 truncate">{selectedDocument.title || selectedDocument.fileName || 'Document View'}</h3>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onClose} 
          className="h-8 text-slate-500 hover:text-slate-800 hover:bg-slate-200 shrink-0 ml-2"
        >
          Close View
        </Button>
      </div>

      <div className="flex-1 relative bg-slate-100 flex flex-col h-full overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-20">
            <Loader2 className="animate-spin text-brand-blue mb-2" size={24} />
            <div className="text-slate-500 font-medium">Loading document securely...</div>
          </div>
        )}
        
        {pdfUrl && (
          <iframe 
            src={`${pdfUrl}#toolbar=0&view=FitH`} 
            className="w-full h-full border-none" 
            title={selectedDocument.title}
          />
        )}
      </div>
    </div>
  );
};
